// Option card for one ACTIVE effect, rendered entirely from the def's params
// metadata (slider/angle/color/fill/segmented/toggle). Every change is an
// idempotent FULL re-apply of the merged params, so slider ticks, color picks
// and preset chips all ride the same engine path (editor.applyTextEffect).
import React, { useState } from 'react';
import { TbSlider, TbColorField, TbSegmented } from '../../primitives/toolbarPrimitives';
import { PaintField } from '../../primitives/PaintPicker';
import { effectDefaults, luminance } from './effectDefs';
import TextureExplorerModal from './TextureExplorerModal';

/** Best solid hex hiding inside a FillValue recipe (objProps.textFill shape). */
function solidFromFillValue(fv) {
  if (!fv) return '#000000';
  if (typeof fv === 'string') return fv;
  if (fv.type === 'solid') return fv.color ?? '#000000';
  const stops = fv.stops ?? fv.colorStops;
  if (stops?.length) return stops[0].color;
  if (fv.from) return fv.from;
  return '#000000';
}

const hexOr = (v, fallback) => (typeof v === 'string' && v.startsWith('#') ? v : fallback);

/**
 * A `type:'fill'` param: the FULL house paint standard in one chip
 * (Sticker's border color — the def names it via strokeFillParam).
 *
 * The value is a FillValue, and a plain string is a valid one — so a solid
 * pick writes the bare hex the PaintPicker emits, exactly the shape every
 * shipped preset and saved recipe already stores. A pattern pick writes
 * {type:'texture', id, scale}: an ID, never a URL (the texture portability
 * law — see fillPresets.js). Gradients ride the def's synchronous
 * strokePaintFromFillValue path. 'Browse all…' stays the escape hatch into
 * the house TextureExplorerModal in pickMode.
 */
function FillParamField({ p, value, label, onChange, onCommit }) {
  const [explorerOpen, setExplorerOpen] = useState(false); // pure UI
  const texId = value?.type === 'texture' ? value.id : null;
  const shown = value == null ? hexOr(solidFromFillValue(value), hexOr(p.default, '#ffffff')) : value;

  return (
    <div className="cce-fx-fill-param">
      <div className="cce-inspector-row">
        <span>{p.label}</span>
        <PaintField
          label={label}
          dataField={p.key}
          value={shown}
          allow={['solid', 'gradient', 'texture']}
          onChange={(next, { live } = {}) =>
            onChange(next, live ? (typeof next === 'string' ? 'soon' : 'defer') : 'now')}
          onCommit={onCommit}
          onBrowseAll={() => setExplorerOpen(true)}
        />
      </div>
      {explorerOpen && (
        <TextureExplorerModal
          pickMode
          pickActiveId={texId}
          onPick={(id) => onChange({ type: 'texture', id, scale: value?.scale ?? 1 }, 'now')}
          onClose={() => setExplorerOpen(false)}
        />
      )}
    </div>
  );
}

export default function EffectOptions({ def, entry, objProps, apply, commitNow, editor }) {
  const [showAdvanced, setShowAdvanced] = useState(false); // pure UI
  const current = { ...effectDefaults(def), ...(entry?.params || {}) };
  const fillSolid = solidFromFillValue(objProps.textFill);

  const applyParams = (params, mode) => apply(() => editor?.applyTextEffect(def.id, params), mode);
  const setParam = (key, v, mode) => applyParams({ ...current, [key]: v }, mode);

  // Smart defaults: a null color param means "derived at apply time" (echo
  // inherits the fill; outline auto-contrasts against it). Show the derived
  // value in the input; write an explicit hex once the user touches it.
  const displayColor = (p) => {
    if (current[p.key] != null) return hexOr(current[p.key], '#000000');
    if (def.id === 'echo') return hexOr(fillSolid, '#000000');
    if (def.id === 'outline') return luminance(fillSolid) > 0.5 ? '#000000' : '#ffffff';
    return hexOr(p.default, '#000000');
  };

  const renderParam = (p) => {
    if (p.type === 'slider' || p.type === 'angle') {
      return (
        <TbSlider
          key={p.key}
          label={`${p.label}: ${current[p.key] ?? p.default}${p.type === 'angle' ? '°' : ''}`}
          value={current[p.key] ?? p.default}
          min={p.min}
          max={p.max}
          step={p.step}
          onChange={(v) => setParam(p.key, v, 'defer')}
          onCommit={commitNow}
        />
      );
    }
    if (p.type === 'color') {
      return (
        <div key={p.key} className="cce-inspector-row">
          <span>{p.label}</span>
          <TbColorField
            label={`${def.label} ${p.label.toLowerCase()}`}
            value={displayColor(p)}
            onChange={(v) => setParam(p.key, v, 'soon')}
          />
        </div>
      );
    }
    // A color OR a pattern. Distinct from 'color' above, which stays exactly
    // as it was for every other param on every other def.
    if (p.type === 'fill') {
      return (
        <FillParamField
          key={p.key}
          p={p}
          value={current[p.key]}
          label={`${def.label} ${p.label.toLowerCase()}`}
          onChange={(v, mode) => setParam(p.key, v, mode)}
          onCommit={commitNow}
        />
      );
    }
    if (p.type === 'segmented') {
      return (
        <div key={p.key} className="cce-inspector-row">
          <span>{p.label}</span>
          <TbSegmented
            label={p.label}
            value={current[p.key] ?? p.default}
            options={p.options}
            onChange={(v) => setParam(p.key, v, 'now')}
          />
        </div>
      );
    }
    // Boolean param (sticker's 'Beveled sticker') — the depth block's
    // checkbox idiom; one history entry per tick.
    if (p.type === 'toggle') {
      return (
        <label key={p.key} className={`cce-poster-check cce-fx-toggle-${p.key}`}>
          <input
            type="checkbox"
            checked={!!current[p.key]}
            onChange={(e) => setParam(p.key, e.target.checked, 'now')}
          />
          <span>{p.label}</span>
        </label>
      );
    }
    return null;
  };

  // A param may declare showIf(params) to opt out of the card when it is
  // meaningless — Outline's gradient stops only exist once the outline IS a
  // gradient. Params without showIf always render, so this is inert for every
  // existing def.
  const visible = def.params.filter((p) => !p.showIf || p.showIf(current));
  const basic = visible.filter((p) => !p.advanced);
  const advanced = visible.filter((p) => p.advanced);

  return (
    <div className="cce-fx-block">
      <div className="cce-fx-block-head">
        <span>{def.label}</span>
        <span>
          <button
            type="button"
            className="cce-fx-textbtn"
            onClick={() => applyParams(effectDefaults(def), 'now')}
          >
            Reset
          </button>
          <button
            type="button"
            className="cce-fx-remove"
            onClick={() => apply(() => editor?.removeTextEffect(def.id), 'now')}
          >
            Remove
          </button>
        </span>
      </div>
      {def.presets && (
        <div className="cce-fx-chips">
          {def.presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="cce-fx-chip"
              onClick={() => applyParams({ ...effectDefaults(def), ...preset.params }, 'now')}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
      {basic.map(renderParam)}
      {advanced.length > 0 && (
        <>
          <button
            type="button"
            className="cce-fx-more"
            onClick={() => setShowAdvanced((s) => !s)}
          >
            {showAdvanced ? '▾' : '▸'} More options
          </button>
          {showAdvanced && advanced.map(renderParam)}
        </>
      )}
    </div>
  );
}
